import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './FormDropdown.scss';

export interface FormDropdownOption {
  value: string;
  label: string;
}

export interface FormDropdownProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: readonly FormDropdownOption[];
  /** Shown when value is empty and no option has value "" */
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  /** Accessible name for the trigger (e.g. filter controls without a visible label). */
  ariaLabel?: string;
  /** When true the dropdown list renders to document.body, escaping any `overflow:hidden`/scroll parents. */
  portal?: boolean;
  /** When true, render a search box at the top of the dropdown that filters options by label. */
  searchable?: boolean;
  /** Placeholder text for the search input. Only used when searchable. */
  searchPlaceholder?: string;
}

const FormDropdown = forwardRef<HTMLButtonElement, FormDropdownProps>(
  function FormDropdown(
    {
      id,
      value,
      onChange,
      onBlur,
      options,
      placeholder = 'Select…',
      disabled = false,
      hasError = false,
      className = '',
      ariaLabel,
      portal = false,
      searchable = false,
      searchPlaceholder = 'Search…',
    },
    ref
  ) {
    const listId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [portalRect, setPortalRect] = useState<{ top: number; left: number; width: number } | null>(null);

    const displayLabel = (() => {
      const match = options.find((o) => o.value === value);
      if (match) return match.label;
      return placeholder;
    })();

    const isPlaceholderDisplay = !options.some((o) => o.value === value);

    const close = useCallback(() => {
      setOpen(false);
      setQuery('');
      onBlur?.();
    }, [onBlur]);

    useEffect(() => {
      if (open && searchable) {
        // Focus the search input on next tick so the dropdown is mounted.
        const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
        return () => window.clearTimeout(t);
      }
      if (!open) setQuery('');
    }, [open, searchable]);

    useEffect(() => {
      const onDoc = (e: MouseEvent) => {
        const t = e.target as Node;
        if (containerRef.current?.contains(t)) return;
        if (listRef.current?.contains(t)) return;
        close();
      };
      if (open) document.addEventListener('mousedown', onDoc);
      return () => document.removeEventListener('mousedown', onDoc);
    }, [open, close]);

    useLayoutEffect(() => {
      if (!open || !portal) {
        setPortalRect(null);
        return;
      }
      const update = () => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPortalRect({ top: r.bottom + 4, left: r.left, width: r.width });
      };
      update();
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      };
    }, [open, portal]);

    useEffect(() => {
      if (disabled) setOpen(false);
    }, [disabled]);

    const pick = (v: string) => {
      onChange(v);
      setOpen(false);
      onBlur?.();
    };

    const setTriggerRef = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    // Don't render the empty/placeholder option in the list — the trigger already
    // shows the placeholder. This keeps the dropdown looking clean.
    const selectableOptions = options.filter((o) => o.value !== '');
    const normalizedQuery = query.trim().toLowerCase();
    const visibleOptions = searchable && normalizedQuery
      ? selectableOptions.filter((o) => o.label.toLowerCase().includes(normalizedQuery))
      : selectableOptions;
    const emptyMessage = selectableOptions.length === 0
      ? 'No items found'
      : 'No results';

    const listEl = open ? (
      <div
        ref={(node) => {
          // Reuse listRef for outside-click detection so portal clicks on the search input don't close the dropdown.
          listRef.current = node as unknown as HTMLUListElement | null;
        }}
        className={`form-dropdown__list-wrap${portal ? ' form-dropdown__list-wrap--portal' : ''}`}
        style={
          portal && portalRect
            ? { top: portalRect.top, left: portalRect.left, width: portalRect.width }
            : undefined
        }
      >
        {searchable && (
          <div className="form-dropdown__search">
            <input
              ref={searchInputRef}
              type="text"
              className="form-dropdown__search-input"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  close();
                }
              }}
            />
          </div>
        )}
        <ul
          id={listId}
          className="form-dropdown__list"
          role="listbox"
        >
          {visibleOptions.length === 0 ? (
            <li className="form-dropdown__option form-dropdown__option--empty" aria-disabled>
              {emptyMessage}
            </li>
          ) : (
            visibleOptions.map((opt) => (
              <li
                key={opt.value === '' ? '__empty' : opt.value}
                role="option"
                aria-selected={value === opt.value}
                className="form-dropdown__option"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt.value)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      </div>
    ) : null;

    return (
      <div ref={containerRef} className={`form-dropdown ${className}`.trim()}>
        <button
          ref={setTriggerRef}
          type="button"
          id={id}
          aria-label={ariaLabel}
          className={`form-dropdown__trigger ${open ? 'form-dropdown__trigger--open' : ''} ${isPlaceholderDisplay ? 'form-dropdown__trigger--placeholder' : ''} ${hasError ? 'form-dropdown__trigger--error' : ''}`}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-invalid={hasError}
          onClick={() => !disabled && setOpen((o) => !o)}
        >
          <span>{displayLabel}</span>
          <svg
            className="form-dropdown__chevron"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            aria-hidden
          >
            <path fill="currentColor" d="M6 8L1 3h10z" />
          </svg>
        </button>
        {portal ? (open && portalRect ? createPortal(listEl, document.body) : null) : listEl}
      </div>
    );
  }
);

export default FormDropdown;
